import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Clock, Award, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MedicoDesempenho = () => {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState('tudo');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNovoCicloModal, setShowNovoCicloModal] = useState(false);
  const [iniciandoCiclo, setIniciandoCiclo] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStats();
  }, [periodo]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/medico/desempenho?periodo=${periodo}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        toast.error('Erro ao carregar estatísticas');
      }
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const handleNovoCiclo = async () => {
    setIniciandoCiclo(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/medico/desempenho/novo-ciclo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Novo ciclo iniciado com sucesso!');
        setShowNovoCicloModal(false);
        fetchStats(); // Reload stats
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao iniciar novo ciclo');
      }
    } catch (error) {
      console.error('Erro ao iniciar novo ciclo:', error);
      toast.error('Erro ao iniciar novo ciclo');
    } finally {
      setIniciandoCiclo(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'melhorou') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === 'piorou') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getNivelColor = (nivel) => {
    if (nivel === 'Avançado') return 'text-green-600 bg-green-50';
    if (nivel === 'Intermediário') return 'text-blue-600 bg-blue-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getAchievementLevelColor = (nivel) => {
    if (nivel === 'Ouro') return 'from-yellow-500 to-yellow-600';
    if (nivel === 'Prata') return 'from-gray-400 to-gray-500';
    if (nivel === 'Bronze') return 'from-orange-600 to-orange-700';
    if (nivel === 'Conquistado') return 'from-green-500 to-green-600';
    return 'from-gray-600 to-gray-700'; // nenhum/bloqueado
  };

  const getAchievementIcon = (nivel) => {
    if (nivel === 'Ouro') return '🥇';
    if (nivel === 'Prata') return '🥈';
    if (nivel === 'Bronze') return '🥉';
    if (nivel === 'Conquistado') return '🏆';
    return '🔒';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-gray-300">Carregando estatísticas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/usuario')}
              className="flex items-center gap-2 text-gray-300 hover:text-white hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white">Meu Desempenho</h1>
              {stats?.reset_at && (
                <p className="text-sm text-gray-400 mt-1">
                  Ciclo atual iniciado em: {new Date(stats.reset_at).toLocaleDateString('pt-BR')}
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={() => setShowNovoCicloModal(true)}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg"
          >
            Começar um novo ciclo de desempenho
          </Button>
        </div>

        {/* Period Filter */}
        <div className="mb-6 flex gap-2">
          <Button
            className={periodo === 'semana' ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700'}
            onClick={() => setPeriodo('semana')}
            size="sm"
          >
            Última semana
          </Button>
          <Button
            className={periodo === 'mes' ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700'}
            onClick={() => setPeriodo('mes')}
            size="sm"
          >
            Último mês
          </Button>
          <Button
            className={periodo === 'tudo' ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700'}
            onClick={() => setPeriodo('tudo')}
            size="sm"
          >
            Tudo
          </Button>
        </div>

        {stats?.total_sessoes === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-12 text-center">
              <BarChart3 className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Nenhuma sessão encontrada</h3>
              <p className="text-gray-400">
                Complete algumas estações para ver suas estatísticas aqui.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* General Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-400">Pontuação Média</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">
                    {stats?.estatisticas_gerais?.pontuacao_media?.toFixed(2)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">de 10 pontos</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-400">Taxa de Acerto</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-white">
                    {stats?.estatisticas_gerais?.taxa_acerto?.toFixed(1)}%
                  </div>
                  <p className="text-xs text-gray-500 mt-1">geral</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-400">Tempo Médio</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-500" />
                    <div className="text-2xl font-bold text-white">
                      {stats?.estatisticas_gerais?.tempo_medio_minutos?.toFixed(0)} min
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">por checklist</p>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-400">Nível Geral</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-500" />
                    <div className={`text-lg font-bold px-3 py-1 rounded-full ${getNivelColor(stats?.estatisticas_gerais?.nivel)}`}>
                      {stats?.estatisticas_gerais?.nivel}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Mini Report */}
            {stats?.mini_report && (
              <Card className="mb-6 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-700/50">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-200">{stats.mini_report}</p>
                </CardContent>
              </Card>
            )}

            {/* Recomendações Section */}
            {stats?.recomendacoes && stats.recomendacoes.length > 0 && (
              <>
                <h2 className="text-xl font-bold text-white mb-4">💡 Recomendações do Sistema</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {stats.recomendacoes.map((rec, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border-blue-700/50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold">!</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="text-white font-semibold text-sm">{rec.alvo}</h4>
                              <span className="px-2 py-0.5 bg-blue-900/50 border border-blue-700/50 rounded text-xs text-blue-300">
                                {rec.tipo}
                              </span>
                            </div>
                            <p className="text-sm text-gray-300 mb-2">{rec.mensagem}</p>
                            {rec.taxa_atual && (
                              <div className="text-xs text-gray-400">
                                Taxa atual: <span className="text-orange-400 font-semibold">{rec.taxa_atual}%</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}

            {/* Conquistas Section */}
            {stats?.conquistas && stats.conquistas.length > 0 && (
              <>
                <h2 className="text-xl font-bold text-white mb-4">🏆 Conquistas</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {stats.conquistas.map((conquista) => (
                    <Card
                      key={conquista.id}
                      className={`border-gray-700 transition-all ${
                        conquista.concluida 
                          ? 'bg-gradient-to-br from-gray-800 to-gray-900 ring-2 ring-yellow-500/50 shadow-lg shadow-yellow-500/20' 
                          : conquista.nivel_atual !== 'nenhum' && conquista.nivel_atual !== 'Bloqueado'
                          ? 'bg-gray-800 hover:bg-gray-750'
                          : 'bg-gray-900/50 opacity-70'
                      }`}
                    >
                      <CardContent className="p-4">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-2xl">{getAchievementIcon(conquista.nivel_atual)}</span>
                              <h4 className="text-white font-semibold text-sm">{conquista.nome}</h4>
                            </div>
                            <p className="text-xs text-gray-400 line-clamp-2">{conquista.descricao}</p>
                          </div>
                        </div>

                        {/* Levels badges */}
                        {conquista.niveis && conquista.niveis.length > 1 && (
                          <div className="flex gap-2 mb-3">
                            {conquista.niveis.map((nivel) => (
                              <div
                                key={nivel}
                                className={`px-2 py-1 rounded text-xs font-semibold ${
                                  conquista.nivel_atual === nivel
                                    ? `bg-gradient-to-r ${getAchievementLevelColor(nivel)} text-white shadow-md`
                                    : 'bg-gray-700 text-gray-500'
                                }`}
                              >
                                {nivel}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Progress bar */}
                        {!conquista.concluida && conquista.niveis.length > 1 && (
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-400">Progresso</span>
                              <span className="text-gray-300 font-semibold">
                                {conquista.progresso_atual} / {conquista.meta_proximo_nivel}
                              </span>
                            </div>
                            {conquista.nivel_atual !== 'nenhum' && conquista.nivel_atual !== 'Bloqueado' && (
                              <p className="text-xs text-purple-400">
                                Próximo nível em: {conquista.meta_proximo_nivel - conquista.progresso_atual}
                              </p>
                            )}
                            <div className="w-full bg-gray-700 rounded-full h-2.5 overflow-hidden">
                              <div
                                className={`bg-gradient-to-r ${getAchievementLevelColor(conquista.nivel_atual === 'nenhum' ? 'Bronze' : conquista.nivel_atual)} h-2.5 rounded-full transition-all duration-500`}
                                style={{
                                  width: `${Math.min((conquista.progresso_atual / conquista.meta_proximo_nivel) * 100, 100)}%`
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Completed badge */}
                        {conquista.concluida && (
                          <div className="bg-green-900/30 border border-green-700/50 rounded px-3 py-1.5 text-center">
                            <span className="text-green-400 text-xs font-semibold">✓ Conquistado!</span>
                          </div>
                        )}

                        {/* Blocked state */}
                        {conquista.nivel_atual === 'Bloqueado' && !conquista.concluida && (
                          <div className="bg-gray-800/50 border border-gray-700 rounded px-3 py-1.5 text-center">
                            <span className="text-gray-500 text-xs font-semibold">🔒 Bloqueado</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}

            {/* Cards por Especialidade */}
            <h2 className="text-xl font-bold text-white mb-4">Desempenho por Especialidade</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {stats?.por_especialidade?.map((esp, idx) => (
                <Card key={idx} className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center justify-between text-white">
                      <span>{esp.especialidade}</span>
                      {getTrendIcon(esp.tendencia)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm text-gray-400">Pontuação Média</p>
                        <p className="text-2xl font-bold text-white">{esp.pontuacao_media}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Estações Concluídas</p>
                        <p className="text-lg font-semibold text-white">{esp.sessoes_concluidas}</p>
                      </div>
                      {esp.ultima_sessao && (
                        <div className="pt-2 border-t border-gray-700">
                          <p className="text-xs text-gray-500">Última estação:</p>
                          <p className="text-sm font-medium text-gray-200">{esp.ultima_sessao.nome}</p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-xs text-gray-500">
                              {new Date(esp.ultima_sessao.data).toLocaleDateString('pt-BR')}
                            </p>
                            <p className="text-sm font-semibold text-white">
                              {esp.ultima_sessao.pontuacao} pts
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Charts */}
            {stats?.evolucao?.length > 0 && (
              <>
                <h2 className="text-xl font-bold text-white mb-4">Evolução das Notas</h2>
                <Card className="mb-6 bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={stats.evolucao}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="data" 
                          tickFormatter={(value) => new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                        />
                        <YAxis domain={[0, 10]} />
                        <Tooltip 
                          labelFormatter={(value) => new Date(value).toLocaleDateString('pt-BR')}
                          formatter={(value) => [`${value} pontos`, 'Pontuação']}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="pontuacao" stroke="#8884d8" strokeWidth={2} name="Pontuação" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Últimas Sessões Bar Chart */}
            {stats?.ultimas_sessoes?.length > 0 && (
              <>
                <h2 className="text-xl font-bold text-white mb-4">Últimas Sessões</h2>
                <Card className="mb-6 bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.ultimas_sessoes}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="estacao" angle={-15} textAnchor="end" height={80} />
                        <YAxis domain={[0, 10]} />
                        <Tooltip formatter={(value) => [`${value} pontos`, 'Pontuação']} />
                        <Legend />
                        <Bar dataKey="pontuacao" fill="#8884d8" name="Pontuação" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>

      {/* Novo Ciclo Modal */}
      <Dialog open={showNovoCicloModal} onOpenChange={setShowNovoCicloModal}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">⚠️ Começar um novo ciclo?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-red-400 font-semibold mb-4">
              ATENÇÃO: Esta ação é irreversível!
            </p>
            <p className="text-gray-300 mb-3">
              Ao iniciar um novo ciclo, TODOS os seus dados de desempenho serão deletados permanentemente:
            </p>
            <ul className="text-gray-300 space-y-2 mb-4 pl-4">
              <li>• Todas as sessões finalizadas</li>
              <li>• Todas as conquistas</li>
              <li>• Todos os gráficos e estatísticas</li>
              <li>• Todo o histórico de tempo e desempenho</li>
            </ul>
            <p className="text-yellow-400 text-sm">
              ⚠️ Os dados NÃO poderão ser recuperados após esta ação.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowNovoCicloModal(false)}
              disabled={iniciandoCiclo}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleNovoCiclo}
              disabled={iniciandoCiclo}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {iniciandoCiclo ? 'Deletando...' : 'Confirmar e Deletar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicoDesempenho;
